import type {
  MaritimeWarningSettings,
  UpdateMaritimeWarningSettingsBody,
} from "@/lib/api/types";
import {
  domainIdsFromEnabledDomains,
  enabledDomainsFromDomainIds,
  maritimeWarningDomainEntries,
  type MaritimeWarningDomainId,
} from "@/lib/maritime-domains";
import { overrideOr, toOverride, type WarningPageCopy } from "@/lib/warning-page-copy";

/**
 * `MaritimeWarningSettings` (`/dashboards/{id}/maritime_warning_settings`, published as
 * `PublishedDashboard.maritime_warnings`) in app terms. Copy is resolved: the `*_override`
 * or else the locale default. The nav label is the `maritime_warnings_page` element title.
 */
export interface MaritimeWarningPageSettings extends WarningPageCopy {
  /** Master switch: off hides the page even when domains are selected. */
  enabled: boolean;
  /** Selected domains, independent of `enabled` (the admin keeps them while off). */
  domainIds: MaritimeWarningDomainId[];
}

/** Tolerates absent or partial payloads; absent means off. */
export function readMaritimeWarningPageSettings(
  settings: Partial<MaritimeWarningSettings> | null | undefined,
  defaults: WarningPageCopy,
): MaritimeWarningPageSettings {
  return {
    enabled: settings?.enabled === true,
    domainIds: domainIdsFromEnabledDomains(settings?.enabled_domains),
    toolbarLabel: overrideOr(settings?.toolbar_label_override, defaults.toolbarLabel),
    title: overrideOr(settings?.title_override, defaults.title),
    description: overrideOr(settings?.description_override, defaults.description),
  };
}

/** Domains that actually show: none while the page is off. */
export function visibleMaritimeDomainIds(
  settings: MaritimeWarningPageSettings,
): MaritimeWarningDomainId[] {
  return settings.enabled ? settings.domainIds : [];
}

/**
 * Gates the maritime warnings page: nav item, route and toolbar button. Needs the master
 * switch on and at least one domain, since a page without domains has no map.
 */
export function hasMaritimeWarningsEnabled(settings: MaritimeWarningPageSettings): boolean {
  return visibleMaritimeDomainIds(settings).length > 0;
}

/** A visible page needs a domain to map, so saving "on" without one is blocked. */
export function isMissingRequiredMaritimeDomain(
  enabled: boolean,
  selectedDomainIds: MaritimeWarningDomainId[],
): boolean {
  return enabled && selectedDomainIds.length === 0;
}

/**
 * Domains to PATCH. The backend rejects an empty `enabled_domains` (422), even with the
 * page off. While off the domain switches are hidden, so an empty selection falls back to
 * the stored domains, then to all domains.
 */
export function maritimeDomainIdsToSave(
  selectedDomainIds: MaritimeWarningDomainId[],
  storedDomainIds: MaritimeWarningDomainId[],
): MaritimeWarningDomainId[] {
  if (selectedDomainIds.length > 0) return selectedDomainIds;
  if (storedDomainIds.length > 0) return storedDomainIds;
  return maritimeWarningDomainEntries().map(({ domain }) => domain.id);
}

/**
 * PATCH body. `enabled` and `enabled_domains` are required, so every save sends them.
 * Copy that is blank or equals the locale default is sent as `null` (no override).
 */
export function maritimeWarningSettingsUpdateBody(
  settings: Partial<MaritimeWarningPageSettings>,
  defaults: WarningPageCopy,
): UpdateMaritimeWarningSettingsBody {
  return {
    enabled: settings.enabled === true,
    enabled_domains: enabledDomainsFromDomainIds(settings.domainIds ?? []),
    toolbar_label_override: toOverride(settings.toolbarLabel, defaults.toolbarLabel),
    title_override: toOverride(settings.title, defaults.title),
    description_override: toOverride(settings.description, defaults.description),
  };
}
